import { useRef } from 'react';
import { Platform } from 'react-native';
import { useGlobalSearchParams } from 'expo-router';
import type { WorkspaceSummary, WorkspaceType } from '@tabbio/types';

function resolveWorkspaceType(workspace: WorkspaceSummary | WorkspaceType) {
  return typeof workspace === 'string' ? workspace : workspace.type;
}

/**
 * `/jobs/[id]` is ambiguous between the (app) and (employer) route groups and
 * the URL parser resolves it to (employer), so personal users deep-loading a
 * job link (or navigating via a `/jobs/<id>` string href: notifications,
 * nova, the apply handoff) land in the employer layout. This hook gives that
 * layout the href to bounce them to the (app) job screen. It must be an
 * OBJECT href: a string href would re-enter the same ambiguous parse, lose
 * again, and strand the user on the workspace default. The id is held in a
 * ref because the layout re-renders with a transient pathname ('/') while its
 * own redirect is in flight: falling through to the workspace default in
 * that window would hijack the job navigation.
 */
export function useJobsDeepLinkPassthrough(pathname: string) {
  const idRef = useRef<string | null>(null);
  const applyRef = useRef<string | null>(null);
  const searchParams = useGlobalSearchParams<{ apply?: string | string[] }>();

  if (pathname.startsWith('/jobs/')) {
    const id = decodeURIComponent(
      pathname.slice('/jobs/'.length).split('/')[0] ?? '',
    );
    if (id) {
      idRef.current = id;
      // The connect-apply intent (?apply=1 from /jobs-handoff) rides the same
      // deep link. Native pathnames strip the query, so capture it from the
      // parsed route params at the same moment the id is captured (and hold it
      // in a ref for the same transient-pathname reason as the id). Web
      // re-adds the full query string below.
      const apply = Array.isArray(searchParams.apply)
        ? searchParams.apply[0]
        : searchParams.apply;
      applyRef.current = apply ?? null;
    }
  }

  if (!idRef.current) {
    return null;
  }

  const params: Record<string, string> = { id: idRef.current };
  if (applyRef.current) {
    params.apply = applyRef.current;
  }
  // Web keeps the query string (e.g. ?catalogJobId=…) across the bounce;
  // native pathnames carry no query so the ref capture above stands in.
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    for (const [key, value] of new URLSearchParams(window.location.search)) {
      if (key !== 'id') {
        params[key] = value;
      }
    }
  }

  return { pathname: '/(app)/jobs/[id]', params } as const;
}

export function getWorkspaceShellPrefix(workspace: WorkspaceSummary | WorkspaceType) {
  return resolveWorkspaceType(workspace) === 'company' ? '/(employer)' : '/(app)';
}

export function getWorkspaceChatHref(
  workspace: WorkspaceSummary | WorkspaceType,
  threadId?: string,
) {
  const base = `${getWorkspaceShellPrefix(workspace)}/chat`;
  return threadId ? `${base}/${threadId}` : base;
}

export function getWorkspaceDefaultHref(workspace: WorkspaceSummary | WorkspaceType) {
  return getWorkspaceChatHref(workspace);
}

export function isWorkspaceShellPath(
  pathname: string | null | undefined,
  workspace: WorkspaceSummary | WorkspaceType,
) {
  if (!pathname) return false;

  const workspaceType = resolveWorkspaceType(workspace);
  const personalPrefixes = [
    '/chat',
    '/cv',
    '/profile',
    '/analytics',
    '/jobs',
  ];
  const companyPrefixes = [
    '/chat',
    '/candidates',
    '/analytics',
    '/company',
    '/jobs',
    '/applications',
  ];

  if (workspaceType === 'company') {
    return companyPrefixes.some((prefix) => pathname.startsWith(prefix));
  }

  return personalPrefixes.some((prefix) => pathname.startsWith(prefix));
}
